import { isAfter, isBefore } from 'date-fns'
import { issueFiltersAtom } from '../../atoms/issueAtom'
import { getIssueDateToFilter, isPreviousIssue } from '../../components/utils/categorizeIssues'
import { EnrichedIssueEntity, IssueEntity } from '../../types/IssueEntity'
import { getSectorIdFromIssueOrWarrant } from './filters/utils/sectors'
import { formatPercentage } from '../../packages/utils/numberFormatters'
import { Fee } from '../../types/FeeEntity'
import { useWatchListFilter } from '../../hooks/useWatchListFilter'
import { InvestorIssue } from 'types/InvestorEntity'

const getAmountInPercentage = (investor: InvestorIssue, totalOfferingSize: number) => {
    const { amountInPercentage, amountInCash } = investor
    if (amountInPercentage !== null && isFinite(amountInPercentage)) {
        return amountInPercentage
    } else if (amountInCash !== null && isFinite(amountInCash / totalOfferingSize)) {
        return (amountInCash / totalOfferingSize) * 100
    }
    return 0
}

export const calculatePriceChange = (
    tradesStat: { closePrice: number | string | null } | undefined,
    subscriptionRate: string | number | null,
) => {
    if (tradesStat === undefined || tradesStat?.closePrice === null) return null
    if (subscriptionRate === null) return null

    const closePrice = +tradesStat?.closePrice
    const subscriptionPrice = +subscriptionRate

    if (closePrice === subscriptionPrice) return 0

    const priceChange = 100 * (closePrice / subscriptionPrice - 1)

    if (isNaN(priceChange) || priceChange === Infinity) return null

    return priceChange
}

export const calculateDiscount = (issue: EnrichedIssueEntity, sharePrice: number | null, isTerp: boolean) => {
    const sharesInUnit = issue.sharesInUnit || 1 // missing for some issue types, but we can use 1 as default
    const { totalOfferedUnits, unitsReceived, rightsRequired, rightsReceived, sharesRequired, unitSubPrice } = issue

    if (typeof sharePrice !== 'number' || sharePrice === 0) {
        return null
    }

    if (!isTerp) {
        return 100 * ((issue.unitSubPrice ?? 0) / sharesInUnit / sharePrice - 1)
    }

    const valueToCompare =
        (((((sharePrice * totalOfferedUnits) / unitsReceived) * rightsRequired) / rightsReceived) * sharesRequired +
            totalOfferedUnits * unitSubPrice) /
        ((((totalOfferedUnits / unitsReceived) * rightsRequired) / rightsReceived) * sharesRequired +
            totalOfferedUnits * sharesInUnit)

    if (isNaN(valueToCompare)) return null

    return 100 * (issue.unitSubPrice / sharesInUnit / valueToCompare - 1)
}

export const calculateDilution = ({
    totalOfferedUnits,
    overAllotmentSize,
    overAllotmentAllocation,
    sharesInUnit,
    unitsReceived,
    rightsRequired,
    rightsReceived,
    sharesRequired,
    unitSubTotal,
}: EnrichedIssueEntity) => {
    const maxDilution =
        (100 * (totalOfferedUnits + overAllotmentSize) * sharesInUnit) /
        ((totalOfferedUnits + overAllotmentSize) * sharesInUnit +
            (totalOfferedUnits / unitsReceived) * (rightsRequired / rightsReceived) * sharesRequired)

    const actualDilution =
        (100 * (unitSubTotal + overAllotmentAllocation) * sharesInUnit) /
        ((unitSubTotal + overAllotmentAllocation) * sharesInUnit +
            (((totalOfferedUnits / unitsReceived) * rightsRequired) / rightsReceived) * sharesRequired)

    return {
        actualDilution: isNaN(actualDilution) ? null : actualDilution,
        maxDilution: isNaN(maxDilution) ? null : maxDilution,
    }
}

export const isSpecialUnitIssue = (issue: Pick<IssueEntity, 'unitExtraContent' | 'sharesInUnit'>) =>
    !!issue.unitExtraContent || issue.sharesInUnit === 0

const hasIssueFinancialAdvisor = (issue: IssueEntity, advisorId: string) => {
    return issue.financialAdvisors.find((advisor) => advisorId.split(',').includes(advisor.advisorId))
}

const hasIssueLegalAdvisor = (issue: IssueEntity, advisorId: string) => {
    return issue.legalAdvisors.find((advisor) => advisorId.split(',').includes(advisor.advisorId))
}

export const getIssueSubscriptionQuotaExclGuarantorsAndCommitments = (issue: EnrichedIssueEntity) => {
    const commitments = getCommitmentsInPercentage(issue)

    if (issue.totalOfferedUnits === null) return null

    const { unitSubWithRights, unitSubWithoutRights } = calcIssueOutcome(issue)
    const comSum = ((commitments?.commitment || 0) / 100) * issue.totalOfferedUnits

    const totalSubUnitsSum = (unitSubWithRights ?? 0) + (unitSubWithoutRights ?? 0)

    const result = ((totalSubUnitsSum - comSum) / (issue.totalOfferedUnits - comSum)) * 100

    return result
}

export const getIssueSubscriptionQuotaExclGuarantors = (issue: EnrichedIssueEntity) => {
    if (issue.totalOfferedUnits === null) return null

    const { unitSubWithoutRights, unitSubWithRights } = calcIssueOutcome(issue)

    return (((unitSubWithRights ?? 0) + (unitSubWithoutRights ?? 0)) / issue.totalOfferedUnits) * 100
}

export const getIssueSubscriptionQuota = ({
    unitSubTotal,
    totalOfferedUnits,
}: Pick<EnrichedIssueEntity, 'unitSubTotal' | 'totalOfferedUnits'>) => {
    if (totalOfferedUnits === null) return null

    return ((unitSubTotal ?? 0) / totalOfferedUnits) * 100
}

export const getIssueSubscribedSize = (issue: IssueEntity) => {
    if ((issue.unitSubTotal === null && issue.secondarySub === null) || issue.unitSubPrice === null) {
        return null
    }
    const unitSubPrice = parseFloat(issue.unitSubPrice)

    const subscribedSize =
        (issue.unitSubTotal ?? 0) +
        (issue.secondarySub ?? 0) +
        (issue.overAllotmentAllocation ?? 0) +
        (issue.overAllotmentAllocationSecondary ?? 0)

    return subscribedSize * unitSubPrice
}

export const getIssueOfferSize = (issue: EnrichedIssueEntity) =>
    (issue.abbTargetSek ?? 0) +
    parseFloat(issue.unitSubPriceSek ?? '0') * ((issue.totalOfferedUnits ?? 0) + (issue.secondaryOffering ?? 0))

export const useFilterIssues = (issues: IssueEntity[], filters: typeof issueFiltersAtom.init) => {
    const { watchListFilter } = useWatchListFilter(filters.onlyInWatchLists.value)

    return issues.filter((issue) => {
        const isEntityAbb = issue.type === 'ABB Directed issue'
        const offeredSize = getIssueOfferSize(issue) / 1000000

        const preMoney = issue.preMoney === null ? null : +issue.preMoney / 1000000

        const mcap = issue.marketcap === null ? null : issue.marketcap / 1000000
        const mcapPrePublication =
            issue.marketcapPrePublication === null ? null : issue.marketcapPrePublication / 1000000
        const mcapPrePublicationFilterValue = issue.type === 'IPO' ? preMoney : mcapPrePublication
        const dateToFilter = new Date(getIssueDateToFilter(issue))

        const guarantorExist = issue.investors.find(
            (investor) => investor.feeLevel > 0 && (investor.amountInCash > 0 || investor.amountInPercentage > 0),
        )

        const noTermsInitially = issue.noTermsInitially
        const sourceTypeTermsExist = issue.sources.some((source) => source.sourceType === 'Terms')

        return (
            watchListFilter(issue) &&
            (filters.sectors.value.length === 0 ||
                (![issue.sectorLevel1, issue.sectorLevel2].includes(null) &&
                    filters.sectors.value.includes(getSectorIdFromIssueOrWarrant(issue)))) &&
            (filters.abb.value.includes('all') ||
                (filters.issueTypes.value.includes('Directed issue') && filters.abb.value.includes('only-abb')
                    ? isEntityAbb
                    : !isEntityAbb)) &&
            (filters.warrants.value.includes('all') ||
                (filters.warrants.value.includes('only-warrants') ? issue.hasWarrants : !issue.hasWarrants)) &&
            (filters.financialAdvisors.value.length === 0 ||
                filters.financialAdvisors.value.some((advisor) => hasIssueFinancialAdvisor(issue, advisor))) &&
            (filters.legalAdvisors.value.length === 0 ||
                filters.legalAdvisors.value.some((advisor) => hasIssueLegalAdvisor(issue, advisor))) &&
            (filters.issueTypes.value.length === 0 ||
                filters.issueTypes.value.includes(isEntityAbb ? 'Directed issue' : issue.type)) &&
            (filters.marketplaces.value.length === 0 || filters.marketplaces.value.includes(issue.marketplaceId)) &&
            offeredSize >= filters.offeredSize.value[0] &&
            (filters.offeredSize.hasMaxValueSet || offeredSize <= filters.offeredSize.value[1]) &&
            (mcap === null ||
                (mcap >= filters.marketSize.value[0] &&
                    (filters.marketSize.hasMaxValueSet || mcap <= filters.marketSize.value[1]))) &&
            (mcapPrePublicationFilterValue === null ||
                (mcapPrePublicationFilterValue >= filters.marketSizePrePublication.value[0] &&
                    (filters.marketSizePrePublication.hasMaxValueSet ||
                        mcapPrePublicationFilterValue <= filters.marketSizePrePublication.value[1]))) &&
            (preMoney === null ||
                (preMoney >= filters.preMoney.value[0] &&
                    (filters.preMoney.hasMaxValueSet || preMoney <= filters.preMoney.value[1]))) &&
            ((isPreviousIssue(issue) &&
                isAfter(dateToFilter, filters.dates.value[0]) &&
                isBefore(dateToFilter, filters.dates.value[1])) ||
                !isPreviousIssue(issue)) &&
            (filters.issueTerms.value.includes('all') ||
                (filters.issueTerms.value.includes('only-issue-terms')
                    ? !noTermsInitially && !sourceTypeTermsExist
                    : noTermsInitially || sourceTypeTermsExist)) &&
            (filters.guarantors.value.includes('all') ||
                (filters.guarantors.value.includes('only-guarantors') ? guarantorExist : !guarantorExist))
        )
    })
}

const calculateCommitment = (issue: Partial<IssueEntity>, filteredInvestors: IssueEntity['investors']) => {
    // Calculate the sum of amountInPercentage
    const sumAmountInPercentage = filteredInvestors.reduce(
        (sum, investor) => sum + (investor.amountInPercentage ?? 0),
        0,
    )

    // Calculate the sum of amountInCash
    // const sumAmountInCash = filteredInvestors.reduce((sum, investor) => sum + investor.amountInCash, 0)

    // Calculate approx_commitment
    // NOTE! The reason the below is commented out is because the API delivers either amountInPercentage or amountInCash,
    // but not both at the same time, however, there is a post-fetch calculation that calculates the missing value.
    // This function assumes that one of them is zero, which is not the case, causing double the correct amount to be returned.
    // TODO: clean up this, either by simplifying this function, or not running any post-fetch calculations
    // or by making sure that the API delivers the complete data always.
    return sumAmountInPercentage // + (100 * sumAmountInCash) / (totalOfferedUnits * unitSubPrice)
}

/*
    Investors har alltid företräde före fälten som heter något med approx_

    Grafen ska visa följande staplar:
    "Totalt åtagande": approx_total_commitment --visas endast om data finns och investors saknas
    "Teckningsåtagande": SUM(investors.amount_in_percentage) + SUM(investors.amount_in_cash)/(offered_unitsunit_sub_price) | approx_commitment -- Beräkningarna sker för investors.fee_level = 0
    "Garant": SUM(investors.amount_in_percentage) + SUM(investors.amount_in_cash)/(offered_unitsunit_sub_price) | approx_commitment -- Dvs precis som teckningsåtagande men investors.fee_level > 1
    "Ej säkerställt" -- resten upp till 100%

*/
export const getCommitmentsInPercentage = (
    issue: Pick<
        IssueEntity,
        'investors' | 'approxCommitment' | 'approxGuarantor' | 'approxTotalCommitment' | 'noInvestorsNamed'
    >,
) => {
    if (issue.investors.length > 0) {
        const filteredInvestors = issue.investors.filter((investor) => investor.feeLevel === 0)

        const commitment =
            calculateCommitment(issue, filteredInvestors) || (issue.approxCommitment ? +issue.approxCommitment : 0)

        const guarantor = calculateCommitment(
            issue,
            issue.investors?.filter((investor) => investor.feeLevel > 0),
        )

        const totalCommitment = commitment + guarantor

        return {
            totalCommitment,
            commitment,
            guarantor,
            notAssured: Math.max(100 - totalCommitment, 0),
        }
    }

    if (issue.approxCommitment && issue.approxGuarantor) {
        const totalCommitment = parseFloat(issue.approxCommitment) + parseFloat(issue.approxGuarantor)

        const commitment = +issue.approxCommitment
        const guarantor = +issue.approxGuarantor

        return {
            totalCommitment,
            commitment,
            guarantor,
            notAssured: Math.max(100 - totalCommitment, 0),
        }
    }

    if (issue.approxTotalCommitment && !issue.investors.length) {
        const totalCommitment = +issue.approxTotalCommitment

        return {
            totalCommitment,
            notAssured: Math.max(100 - totalCommitment, 0),
        }
    }

    if (issue.noInvestorsNamed) {
        return {
            totalCommitment: 0,
            notAssured: 100,
        }
    }

    return null
}

export const calculateSumSubscriptionCommitmentInPercentage = ({
    investors,
    totalOfferingSize,
}: {
    investors: Pick<IssueEntity['investors'][number], 'amountInPercentage' | 'amountInCash' | 'feeLevel'>[]
    totalOfferingSize: number
}) =>
    investors.reduce((acc, investor) => {
        const amountInPercentage =
            investor.amountInPercentage !== null && isFinite(investor.amountInPercentage)
                ? investor.amountInPercentage
                : (investor.amountInCash! / totalOfferingSize) * 100
        return investor.feeLevel === 0 ? acc + amountInPercentage : acc
    }, 0)

export const getSummarizedFeeLevels = (investors: IssueEntity['investors'], totalOfferingSize: number) =>
    investors
        .reduce(
            (acc, curr) => {
                const existingFeeLevelIndex = acc.findIndex(
                    (commitmentLevel) => commitmentLevel.feeLevel === curr.feeLevel,
                )

                const amountInPercentage = getAmountInPercentage(curr, totalOfferingSize)

                if (existingFeeLevelIndex === -1) {
                    acc.push({
                        amountInCash: curr.amountInCash ?? 0,
                        amountInPercentage,
                        feeLevel: curr.feeLevel,
                    })
                } else {
                    const existingCommitmentLevelObject = acc[existingFeeLevelIndex]
                    acc[existingFeeLevelIndex] = {
                        ...existingCommitmentLevelObject,
                        amountInCash: existingCommitmentLevelObject.amountInCash + (curr.amountInCash ?? 0),
                        amountInPercentage: existingCommitmentLevelObject.amountInPercentage + amountInPercentage,
                    }
                }

                return acc
            },
            [] as Array<{ amountInCash: number; amountInPercentage: number; feeLevel: number }>,
        )
        .sort((a, b) => a.feeLevel - b.feeLevel)

export const getLevelName = (level: number, maxLevel: number) => {
    if (level === 0) {
        return 'Teckningsåtagare'
    }
    if (level === 1 && maxLevel === 1) {
        return 'Garantiåtagare'
    }
    if (level === 1 && maxLevel > 1) {
        return 'Bottengaranti'
    }
    if (level === 2 && maxLevel === 2) {
        return 'Toppgaranti'
    }
    if (level > 1 && maxLevel > 2) {
        return `Nivå ${level}`
    }
    return '-'
}

export const formatFeeLevel = (
    { fees, isUnit }: { fees: Fee[]; isUnit: boolean },
    level: number,
    maxFeeLevel: number,
    locale: string,
) => {
    if (!fees || level >= maxFeeLevel) return null

    const feesWithLevel = fees.find((fee) => fee.feeLevel === level + 1)

    if (!feesWithLevel) return null

    let result = ''

    // Check if fee_cash exists
    if (feesWithLevel.feeCash) {
        result +=
            formatPercentage(feesWithLevel.feeCash, {
                decimals: 0,
                locale,
            }) + ' kontant'
    }

    // Check if both fee_cash and fee_units exist but not fee_both
    if (feesWithLevel.feeCash && feesWithLevel.feeUnits && !feesWithLevel.feeBoth) {
        result += ' eller '
    }

    // Check if fee_both exists (insert "och" if true)
    if (feesWithLevel.feeBoth) {
        result += ' och '
    }

    // Add a line break if there was a previous value
    if (feesWithLevel.feeUnits && result) {
        result += '\n'
    }

    // Check if fee_units exists and append it to a new line
    if (feesWithLevel.feeUnits) {
        result +=
            formatPercentage(feesWithLevel.feeUnits, {
                decimals: 0,
                locale,
            }) +
            ' i ' +
            (isUnit ? 'units' : 'aktier')
    }

    if (!feesWithLevel.feeCash && !feesWithLevel.feeUnits) {
        result += 'Vederlagsfri'
    }

    return result
}

export const calculateIssueTotalOfferingSize = ({
    totalOfferedUnits,
    abbTarget,
    secondaryOffering,
    unitSubPrice,
}: Pick<EnrichedIssueEntity, 'totalOfferedUnits' | 'abbTarget' | 'secondaryOffering' | 'unitSubPrice'>) => {
    const offeredSize = (totalOfferedUnits ?? 0) + (secondaryOffering ?? 0)
    const totalOfferingSize = (abbTarget ?? 0) + offeredSize * parseFloat(unitSubPrice ?? '0')

    return { offeredSize, totalOfferingSize }
}

export const calculateIpoOutcome = (issue: IssueEntity) => {
    const unitSubPrice = issue.unitSubPrice !== null ? issue.unitSubPrice : 0
    const unitSubTotal = issue.unitSubTotal !== null ? issue.unitSubTotal : 0
    const secondarySub = issue.secondarySub !== null ? issue.secondarySub : 0
    const overAllotmentAllocation = issue.overAllotmentAllocation !== null ? issue.overAllotmentAllocation : 0
    const overAllotmentAllocationSecondary =
        issue.overAllotmentAllocationSecondary !== null ? issue.overAllotmentAllocationSecondary : 0

    const subTotal = unitSubTotal + secondarySub
    const { totalOfferingSize, offeredSize } = calculateIssueTotalOfferingSize(issue)

    const feeLevels = getSummarizedFeeLevels(issue.investors, totalOfferingSize)

    const pctSubscribedGuarantors = feeLevels.reduce((acc, curr) => {
        if (curr.feeLevel === 0) return acc
        acc += curr.amountInPercentage
        return acc
    }, 0)

    const detailedGuarantorData = feeLevels
        .filter(({ feeLevel }) => feeLevel > 0)
        .reduce((acc, curr) => {
            const sumGuarantorPercentage = feeLevels.reduce((sum, fee) => {
                if (fee.feeLevel > curr.feeLevel) {
                    sum += fee.amountInPercentage
                }
                return sum
            }, 0)

            const pctOfGuarantSub = (pctSubscribedGuarantors - sumGuarantorPercentage) / curr.amountInPercentage
            const cappedPctOfGuarantSub = Math.max(Math.min(pctOfGuarantSub, 1), 0)
            const pctOfIssue = (cappedPctOfGuarantSub * curr.amountInPercentage) / 100
            const numberOfUnits = offeredSize * pctOfIssue
            const amount = numberOfUnits * unitSubPrice

            acc.push({
                pctOfIssue: pctOfIssue,
                pctOfGuarantSub: cappedPctOfGuarantSub,
                numberOfUnits,
                amount,
                feeLevel: curr.feeLevel,
            })

            return acc
        }, [])

    const pctSubCommitment = feeLevels.find(({ feeLevel }) => feeLevel === 0)?.amountInPercentage ?? 0
    const unitSubCommitment = (pctSubCommitment / 100) * offeredSize
    const unitSubGuarantor = (issue.unitSubGuarantor ?? 0) + ((issue.unitPctSubGuarantor ?? 0) / 100) * offeredSize
    const unitSubPublic = subTotal - unitSubCommitment - unitSubGuarantor

    return {
        pctSubCommitment,
        pctSubscribedPublic: Math.round(((unitSubPublic * 100) / offeredSize) * 100) / 100,
        pctSubscribedGuarantors: Math.round(((unitSubGuarantor * 100) / offeredSize) * 100) / 100,
        pctNotSubscribed: Math.max(((offeredSize - subTotal) * 100) / offeredSize, 0),
        detailedGuarantorData,
        overAllotment: overAllotmentAllocation + overAllotmentAllocationSecondary,
        unitSubCommitment: Math.round(unitSubCommitment) || 0,
        unitSubPublic: Math.round(unitSubPublic) || 0,
        unitSubGuarantor: Math.round(unitSubGuarantor) || 0,
        unitSubNotSubscribed: Math.round(offeredSize - subTotal) || 0,
    }
}

export const calcIssueOutcome = (issue: EnrichedIssueEntity) => {
    const unitSubPrice = issue.unitSubPrice !== null ? issue.unitSubPrice : 0
    const totalOfferedUnits = issue.totalOfferedUnits !== null ? issue.totalOfferedUnits : 0
    const overAllotmentAllocation = issue.overAllotmentAllocation !== null ? issue.overAllotmentAllocation : 0
    const overAllotmentAllocationSecondary =
        issue.overAllotmentAllocationSecondary !== null ? issue.overAllotmentAllocationSecondary : 0

    const totalOfferingSize = totalOfferedUnits * unitSubPrice

    const pctSubscribedWithRights =
        issue.unitPctSubWithRights || (issue.unitSubWithRights! / totalOfferedUnits) * 100 || 0
    const pctSubscribedWithoutRights =
        issue.unitPctSubWithoutRights || (issue.unitSubWithoutRights! / totalOfferedUnits) * 100 || 0
    const pctSubscribedGuarantors =
        issue.unitPctSubGuarantor || (issue.unitSubGuarantor! / totalOfferedUnits) * 100 || 0
    const pctUnsubscribed = Math.max(
        100 - pctSubscribedWithRights - pctSubscribedWithoutRights - pctSubscribedGuarantors,
        0,
    )

    const feeLevels = getSummarizedFeeLevels(issue.investors, totalOfferingSize).filter(({ feeLevel }) => feeLevel > 0)

    const pctGuarantorCommitment = feeLevels.reduce((sum, investor) => (sum += investor.amountInPercentage), 0)

    const detailedGuarantorData = feeLevels.reduce((acc, curr) => {
        const sumGuarantorPercentage = feeLevels.reduce((sum, fee) => {
            if (fee.feeLevel > curr.feeLevel) {
                sum += fee.amountInPercentage
            }
            return sum
        }, 0)

        const pctOfGuarantSub = (pctSubscribedGuarantors - sumGuarantorPercentage) / curr.amountInPercentage
        const cappedPctOfGuarantSub = Math.max(Math.min(pctOfGuarantSub, 1), 0)
        const pctOfIssue = (cappedPctOfGuarantSub * curr.amountInPercentage) / 100
        const numberOfUnits = totalOfferedUnits * pctOfIssue
        const amount = numberOfUnits * unitSubPrice

        acc.push({
            pctOfIssue: pctOfIssue,
            pctOfGuarantSub: cappedPctOfGuarantSub,
            numberOfUnits,
            amount,
            feeLevel: curr.feeLevel,
        })

        return acc
    }, [])

    const result = {
        pctSubscribedWithRights: pctSubscribedWithRights !== null ? parseFloat(pctSubscribedWithRights) : null,
        pctSubscribedWithoutRights: pctSubscribedWithoutRights !== null ? parseFloat(pctSubscribedWithoutRights) : null,
        pctSubscribedGuarantors: pctSubscribedGuarantors !== null ? parseFloat(pctSubscribedGuarantors) : null,
        pctGuarantorCommitment: pctGuarantorCommitment !== null ? parseFloat(pctGuarantorCommitment) : null,
        pctUnsubscribed,
        detailedGuarantorData,
        overAllotment: overAllotmentAllocation + overAllotmentAllocationSecondary,
        unitSubTotal: 0,
        unitSubWithRights: 0,
        unitSubWithoutRights: 0,
        unitSubGuarantor: 0,
        unitSubUnsubscribed: 0,
    }
    if (
        issue.unitSubWithRights !== null &&
        issue.unitSubWithoutRights !== null &&
        issue.unitSubGuarantor !== null &&
        issue.unitSubTotal !== null
    ) {
        return {
            ...result,
            unitSubTotal: issue.unitSubTotal,
            unitSubWithRights: issue.unitSubWithRights,
            unitSubWithoutRights: issue.unitSubWithoutRights,
            unitSubGuarantor: issue.unitSubGuarantor,
            unitSubUnsubscribed: totalOfferedUnits - issue.unitSubTotal,
        }
    } else if (issue.unitSubTotal !== null) {
        return {
            ...result,
            unitSubTotal: issue.unitSubTotal,
            unitSubWithRights: Math.round((pctSubscribedWithRights / 100) * totalOfferedUnits),
            unitSubWithoutRights: Math.round((pctSubscribedWithoutRights / 100) * totalOfferedUnits),
            unitSubGuarantor: Math.round((pctSubscribedGuarantors / 100) * totalOfferedUnits),
            unitSubUnsubscribed: totalOfferedUnits - issue.unitSubTotal,
            approximation: true,
        }
    } else {
        console.log(`calcOutcome(): Can't find a combination of fields to calculate outcome`)
    }
    return result
}

export const simplifyFraction = (valueOne: number, valueTwo: number) => {
    for (let i = Math.max(valueOne, valueTwo); i > 1; i--) {
        if (valueOne % i == 0 && valueTwo % i == 0) {
            valueOne /= i
            valueTwo /= i
        }
    }
    return { valueOne, valueTwo }
}

export const randomBlue = () => {
    return 'rgb(0, 0, ' + Math.floor(Math.random() * 255) + ')'
}
